import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useMemo } from "react";

import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import { Button } from "@/design-system/Button";
import { NumericBadge } from "@/design-system/NumericBadge";
import { useBool } from "@/hooks/useBool";
import { AnalyticsScopeProvider } from "@/modules/analytics/AnalyticsScopeContext";
import { getActiveTicketFilterCount } from "@/modules/tickets/pages/list/helpers";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { useTranslation } from "@/translations";

import { PortfolioProjectFilterDrawer } from "./PortfolioProjectFilterDrawer";
import { usePortfolioAnalyticsFilters } from "./usePortfolioAnalyticsFilters";

/** Provides portfolio analytics pages with the active project scope and shared project filter controls. */
export function PortfolioAnalyticsScope({ children }: { children: ReactNode }): React.ReactNode {
  const { t } = useTranslation();
  const selfAccountQueries = useSelfAccountQueries();
  const { data: projects = [], isLoading } = useQuery(selfAccountQueries.listLinkedProjects());
  const [filters, setFilters] = usePortfolioAnalyticsFilters();
  const [isFilterOpened, filterOpenHandlers] = useBool();
  const filteredProjectIds = useMemo(
    () => filterProjectsByFilters(projects, filters).map((project) => project.id),
    [filters, projects],
  );
  const activeFilterCount = getActiveTicketFilterCount(filters);

  return (
    <AnalyticsScopeProvider
      value={{
        type: "portfolio",
        projectIds: filteredProjectIds,
        isLoading,
        projectFilter: {
          projects,
          value: filters,
          activeCount: activeFilterCount,
          onChange: setFilters,
        },
        filterAction: (
          <Button
            variant="soft"
            icon={<FilterFunnel01Icon />}
            onClick={filterOpenHandlers.setTrue}
            data-testid="portfolio-analytics-project-filter"
          >
            <span className="flex items-center gap-space-sm">
              {t("common.action.filter")}
              {activeFilterCount > 0 && <NumericBadge count={activeFilterCount} variant="filled" />}
            </span>
          </Button>
        ),
      }}
    >
      {children}
      <PortfolioProjectFilterDrawer
        projects={projects}
        selectedFilter={filters}
        onUpdate={setFilters}
        includeProjectSelection
        isOpened={isFilterOpened}
        onOpenChange={filterOpenHandlers.set}
      />
    </AnalyticsScopeProvider>
  );
}
